// ============================================================================
// "Download PDF" for a Transfer Orders drill-down — a receiving checklist the warehouse team works
// off, GROUPED BY LEAD. It prints the scope (dispatch date, receiving warehouse) and grand totals at
// the top, then per lead: a "Lead id:" band, and under it one tick-box row per PO (transfer leg) with
// its item names and weight. Already-received (transferred) legs are dropped; errored legs are kept
// (a stuck leg still has to be received).
//
// ASCII only in the text: jsPDF's built-in Helvetica is WinAnsi, so an arrow or any non-Latin-1
// glyph garbles the line — the "received" box is DRAWN (a rectangle), never a character.
// ============================================================================
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { TransferOrderRow } from "../control_tower.api";

// The ops team works in IST — stamp the sheet in IST regardless of the downloader's machine timezone.
dayjs.extend(utc);
dayjs.extend(timezone);
const IST = "Asia/Kolkata";

const BRAND: [number, number, number] = [36, 170, 183]; // colorPrimary #24AAB7
const MARGIN = 14;
const PAGE_BOTTOM = 282; // A4 portrait is 297mm tall — leave room for the footer

// Move onto a new page when `needed` mm of content would not fit.
const ensureSpace = (doc: jsPDF, y: number, needed: number): number =>
  y + needed > PAGE_BOTTOM ? (doc.addPage(), MARGIN + 6) : y;

const lastY = (doc: jsPDF, fallback: number): number =>
  (doc as any).lastAutoTable?.finalY ?? fallback;

// Decimals arrive as numbers or Django-serialised strings ("52.92"); coerce, else null.
const coerce = (v: number | string | null | undefined): number | null => {
  const n = typeof v === "number" ? v : v != null && v !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

const fmtKg = (v: number | string | null | undefined): string => {
  const n = coerce(v);
  return n != null ? `${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg` : "-";
};

const fmtQty = (v: number | string | null | undefined): string => {
  const n = coerce(v);
  return n != null ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "-";
};

// A backend ISO datetime as an IST day, e.g. "20 Sep", or "" when null.
const fmtDayIST = (v: string | null): string => {
  if (!v) return "";
  const d = dayjs.utc(v);
  return d.isValid() ? d.tz(IST).format("DD MMM") : "";
};

// The transfer's aging line — when the order was placed and when this allocation was created.
const agingLine = (r: TransferOrderRow): string => {
  const parts: string[] = [];
  const ordered = fmtDayIST(r.order_placed_time);
  const allocated = fmtDayIST(r.allocation_created_time);
  if (ordered) parts.push(`Ordered ${ordered}`);
  if (allocated) parts.push(`Allocated ${allocated}`);
  return parts.join(" · ");
};

// One leg's weight: the server total when present, else the sum of its cargo lines.
const legWeight = (r: TransferOrderRow): number | null => {
  const t = coerce(r.cargo_weight_kg);
  if (t != null) return t;
  if (r.cargo_lines?.length)
    return r.cargo_lines.reduce((s, l) => s + (coerce(l.weight_kg) ?? 0), 0);
  return null;
};

// Build the tick-box table rows for one lead's legs. The PO column stacks, top to bottom: the
// allocation no. (bold), the aging dates (normal), and — when blocked upstream — an amber "Awaiting
// GRN at source" note. Only the checkbox spans; weight shows once on the first sub-row. Every other
// cell is given explicitly so columns don't shift. Returns the body plus the body-row indices that
// start/end each leg, so the caller can draw a divider only BETWEEN legs (not between sub-rows).
const buildLegBody = (
  legs: TransferOrderRow[]
): { rows: RowInput[]; legStart: Set<number>; legEnd: Set<number> } => {
  const rows: RowInput[] = [];
  const legStart = new Set<number>();
  const legEnd = new Set<number>();
  legs.forEach((leg) => {
    const start = rows.length;
    const poCol: { text: string; bold?: boolean; amber?: boolean }[] = [
      { text: leg.po_number || "PO -", bold: true },
    ];
    const dates = agingLine(leg);
    if (dates) poCol.push({ text: dates });
    if (leg.previous_warehouse_grn_marked === false)
      poCol.push({ text: "Awaiting GRN at source", amber: true });

    const itemCol = leg.cargo_lines?.length
      ? leg.cargo_lines.map((l) => ({ name: l.name, qty: String(l.qty) }))
      : [{ name: "No item lines.", qty: "" }];

    const rowCount = Math.max(poCol.length, itemCol.length);
    for (let r = 0; r < rowCount; r += 1) {
      const row: RowInput = [];
      if (r === 0) row.push({ content: "", rowSpan: rowCount }); // Received (checkbox drawn)
      const po = poCol[r];
      row.push(
        po
          ? {
              content: po.text,
              styles: {
                ...(po.bold ? { fontStyle: "bold" as const } : {}),
                ...(po.amber ? { textColor: [191, 90, 0] as [number, number, number] } : {}),
              },
            }
          : { content: "" }
      );
      row.push(r === 0 ? { content: fmtKg(legWeight(leg)), styles: { halign: "right" as const } } : { content: "" });
      const it = itemCol[r];
      row.push(it ? { content: it.name } : { content: "" });
      row.push(it ? it.qty : "");
      rows.push(row);
    }
    legStart.add(start);
    legEnd.add(rows.length - 1);
  });
  return { rows, legStart, legEnd };
};

// A lead group as the checklist needs it — its non-transferred legs (built by the caller).
export interface TransferPdfGroup {
  lead_id: string | null;
  oms_sales_order_id: number | null;
  legs: TransferOrderRow[];
}

export interface TransferPdfMeta {
  // The warehouse the sheet is scoped to, as a { label, value } pair (e.g. label "Origin Warehouse",
  // value "rebound exports"). Omitted for the "All" view — then no warehouse line is printed.
  warehouse?: { label: string; value: string };
  dispatchFrom: string; // YYYY-MM-DD
  dispatchTo: string;
}

// A dispatch window as one readable line — a single day when from === to, else a range.
const fmtDispatch = (from: string, to: string): string => {
  const f = dayjs(from);
  const t = dayjs(to);
  const fmt = "DD MMM YYYY";
  if (!f.isValid() || !t.isValid()) return `${from || "-"} to ${to || "-"}`;
  return f.isSame(t, "day") ? f.format(fmt) : `${f.format(fmt)} to ${t.format(fmt)}`;
};

export function downloadTransferOrdersPdf(groups: TransferPdfGroup[], meta: TransferPdfMeta): void {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ── Header: what this sheet is, dispatch date first, then grand totals (values in BOLD) ──
  doc.setFontSize(15);
  doc.setTextColor(20);
  doc.text("Transfer Orders - Receiving Checklist", MARGIN, 16);

  const allLegs = groups.flatMap((g) => g.legs);
  const totalWeight = allLegs.reduce((s, r) => s + (legWeight(r) ?? 0), 0);
  const totalQty = allLegs.reduce((s, r) => s + (coerce(r.total_quantity) ?? 0), 0);
  const poCount = allLegs.length;

  const metaPairs: { label: string; value: string }[] = [
    { label: "Dispatch date: ", value: fmtDispatch(meta.dispatchFrom, meta.dispatchTo) },
    // Warehouse line only when scoped to one (omitted for the "All" view).
    ...(meta.warehouse ? [{ label: `${meta.warehouse.label}: `, value: meta.warehouse.value }] : []),
    { label: "Total weight: ", value: fmtKg(totalWeight) },
    { label: "Total quantity: ", value: fmtQty(totalQty) },
    {
      label: "",
      value: `${poCount} PO${poCount === 1 ? "" : "s"} across ${groups.length} lead${
        groups.length === 1 ? "" : "s"
      }`,
    },
  ];
  doc.setFontSize(9);
  metaPairs.forEach((pair, i) => {
    const lineY = 24 + i * 4.6;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(110);
    doc.text(pair.label, MARGIN, lineY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40);
    doc.text(pair.value, MARGIN + doc.getTextWidth(pair.label), lineY);
  });
  doc.setFont("helvetica", "normal");

  let y = 24 + metaPairs.length * 4.6 + 4;

  if (groups.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(110);
    doc.text("Nothing to receive in this list.", MARGIN, y);
  }

  // ── Body: one band per lead, then a tick-box row per PO under it ──
  groups.forEach((group) => {
    const groupWeight = group.legs.reduce((s, r) => s + (legWeight(r) ?? 0), 0);
    const groupQty = group.legs.reduce((s, r) => s + (coerce(r.total_quantity) ?? 0), 0);

    y = ensureSpace(doc, y, 22); // never orphan a lead band at the page foot
    doc.setFillColor(...BRAND);
    doc.rect(MARGIN, y - 4.5, 182, 7, "F");
    doc.setFontSize(10);
    doc.setTextColor(255);
    const bandRight = group.oms_sales_order_id ? `SO #${group.oms_sales_order_id}   ` : "";
    doc.text(
      `Lead id: ${group.lead_id || "-"}   ${bandRight}·   Qty ${fmtQty(groupQty)} · ${fmtKg(groupWeight)}`,
      MARGIN + 2,
      y
    );
    y += 6;

    const { rows: bodyRows, legStart, legEnd } = buildLegBody(group.legs);
    const BORDER = 0.1;
    const LINE: [number, number, number] = [217, 217, 217];
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      // Borders are set per cell (didParseCell) so a horizontal divider falls only BETWEEN legs, not
      // between a leg's own sub-rows (PO no. / dates / items). valign top aligns Qty with its item.
      theme: "plain",
      head: [["Received", "PO", "Weight", "Item", "Qty"]],
      body: bodyRows,
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak", textColor: 40, valign: "top", lineColor: LINE },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: 40,
        fontStyle: "bold",
        lineWidth: { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER },
      },
      columnStyles: {
        0: { cellWidth: 18, halign: "center" },
        1: { cellWidth: 40 }, // PO — allocation no. (bold, per cell) with the dates beneath (normal)
        2: { cellWidth: 18, halign: "right" }, // Weight
        3: { cellWidth: 96 }, // Item
        4: { cellWidth: 10, halign: "right", fontStyle: "bold" }, // Qty — bold, narrower
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        // Verticals on every cell; horizontals only at a leg's top/bottom, so sub-rows within a leg
        // have no divider. The spanning checkbox cell (col 0) is a full leg box.
        if (data.column.index === 0) {
          data.cell.styles.lineWidth = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
        } else {
          data.cell.styles.lineWidth = {
            left: BORDER,
            right: BORDER,
            top: legStart.has(data.row.index) ? BORDER : 0,
            bottom: legEnd.has(data.row.index) ? BORDER : 0,
          };
        }
      },
      didDrawCell: (data) => {
        // Draw an empty square in the body's first column — the box the team ticks on receipt.
        // Near the top of the (row-spanning) cell so it aligns with the PO / first item line.
        if (data.section === "body" && data.column.index === 0) {
          const size = 4.5;
          const cx = data.cell.x + data.cell.width / 2 - size / 2;
          const cy = data.cell.y + 2.5;
          doc.setDrawColor(90);
          doc.setLineWidth(0.3);
          doc.rect(cx, cy, size, size);
        }
      },
    });
    y = lastY(doc, y) + 5;
  });

  // ── Footer: page numbers + when it was printed (a stale receiving sheet is a real hazard) ──
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Downloaded ${dayjs().tz(IST).format("DD MMM YYYY, hh:mm A")} IST - page ${p} of ${pages}`,
      MARGIN,
      290
    );
  }

  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const stamp = dayjs().format("YYYY-MM-DD");
  doc.save(`transfer-orders-${slug(meta.warehouse?.value ?? "all")}-${stamp}.pdf`);
}
